// Considered: GlyphPickerMolecule alone — rejected because it has no picture, upload, remove or sphere provenance.
// Considered: the profile avatar menu (user-identity) — rejected because it is organism-specific and has no glyph.
// This control composes both behaviours rather than duplicating either. See spec Reuse Audit.
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { Toast } from 'primereact/toast';
import {
  isRetryableSphereVisualError,
  type PickerValue,
  type SphereVisualChange,
  type SphereVisualError,
  type SphereVisualPictureLimits,
  type SphereVisualSaved,
  type SphereVisualSchemaType,
  type SphereVisualWrite,
  type UseSphereVisualResult,
} from '../../../hooks/useSphereVisual';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import GlyphPickerMolecule from '../studio/GlyphPickerMolecule';
import UploadAvatarDialogMolecule from '../user-identity/UploadAvatarDialogMolecule';
import OverlayPanelAtom, { type OverlayPanelHandle } from '../../atoms/OverlayPanel/OverlayPanelAtom';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import './SphereVisualControlMolecule.scss';

export interface SphereVisualControlMoleculeProps {
  /**
   * The sphere's visual state, from `useSphereVisual`. The owner mounts the hook once and passes
   * the same result to every control showing this sphere's mark, so they never disagree.
   */
  visual: UseSphereVisualResult;
  sphereId: string;
  /** Display name, used in the remove-picture confirmation. */
  sphereName?: string;
  sphereType: SphereVisualSchemaType;
  /** The type's display name for provenance copy; falls back to its system name. */
  sphereTypeLabel?: string;
  /** Without edit permission the mark is shown but cannot be changed. */
  readOnly?: boolean;
  /**
   * `header`: an avatar-sized button that opens the panel in an overlay. `row`: the mark and the
   * same panel inline, in a fieldset labelled "Picture or glyph".
   */
  variant?: 'header' | 'row';
  /** For `row`: the id of the visible label the surrounding layout renders. Without it the row shows its own. */
  labelledBy?: string;
  onSaveComplete?: () => void;
}

/** What this control instance asked for, so it alone closes its dialogs and shows the toast. */
interface Intent {
  closeUpload?: boolean;
  closeRemove?: boolean;
}

const TOAST_KEY: Partial<Record<SphereVisualChange, string>> = {
  glyph: 'glyph',
  glyph_and_colour: 'glyph',
  colour: 'colour',
  reset: 'reset',
  picture_added: 'pictureAdded',
  picture_replaced: 'pictureReplaced',
  picture_removed: 'pictureRemoved',
};

/** Ledger write-tier and source denials with copy of their own; any other refusal uses `refused.other`. */
const REFUSAL_REASONS = new Set(['aspect_read_only', 'aspect_source_not_allowed']);

const BYTES_PER_MB = 1024 * 1024;

/** The name a person knows an image format by, from its MIME type. */
const FORMAT_NAMES: Record<string, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'image/svg+xml': 'SVG',
};
const formatName = (mimeType: string): string =>
  FORMAT_NAMES[mimeType] ?? (mimeType.split('/')[1] ?? mimeType).toUpperCase();

/** The slot's limits as the upload copy names them; an absent limit is left out of the copy. */
function describeLimits(limits: SphereVisualPictureLimits | null): { types: string[] | null; size: number | null } {
  return {
    types: limits?.mimeTypes?.length ? limits.mimeTypes.map(formatName) : null,
    size: limits?.maxSize ? limits.maxSize / BYTES_PER_MB : null,
  };
}

export const SphereVisualControlMolecule: React.FC<SphereVisualControlMoleculeProps> = ({
  visual,
  sphereId,
  sphereName,
  sphereType,
  sphereTypeLabel,
  readOnly = false,
  variant = 'header',
  labelledBy,
  onSaveComplete,
}) => {
  const { t } = useTranslation();
  const toastRef = useRef<Toast>(null);
  const overlayRef = useRef<OverlayPanelHandle>(null);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const intentRef = useRef<Intent | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const labelId = useId();

  const { resolved, own, picture, status, operation, error, canUploadPicture, pictureLimits, lastSaveId } = visual;
  const visualRef = useRef(visual);
  visualRef.current = visual;
  const typeLabel = sphereTypeLabel ?? sphereType.system_name;

  // The header panel is portalled to the body, so opening it moves focus to its first action; the
  // overlay owns Escape and click-outside, and closing it returns focus to the avatar button.
  useDialogFocus(panelOpen && variant === 'header', panelRef, { trapFocus: false });

  // Undo reverts the latest write only, so a toast stays only while its write is the latest one —
  // whichever control showing this sphere made the next write.
  const toastSaveIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (toastSaveIdRef.current !== null && toastSaveIdRef.current !== lastSaveId) {
      toastRef.current?.clear();
      toastSaveIdRef.current = null;
    }
  }, [lastSaveId]);

  const succeeded = useCallback(
    (intent: Intent, saved: SphereVisualSaved) => {
      if (intent.closeUpload) setUploadOpen(false);
      if (intent.closeRemove) setRemoveOpen(false);
      toastRef.current?.clear();
      toastSaveIdRef.current = null;
      const toastKey = TOAST_KEY[saved.change];
      if (toastKey) {
        toastSaveIdRef.current = saved.id;
        toastRef.current?.show({
          severity: 'success',
          life: 8000,
          content: (
            <output className="sphere-visual-control__toast">
              <span>{t(`sphere.visual.toast.${toastKey}`, { type: typeLabel })}</span>
              {saved.undoable && (
                <ButtonAtom
                  variant="text"
                  label={t('sphere.visual.undo')}
                  onClick={() => {
                    toastRef.current?.clear();
                    toastSaveIdRef.current = null;
                    intentRef.current = null;
                    void visualRef.current.undo();
                  }}
                />
              )}
            </output>
          ),
        });
      }
      onSaveComplete?.();
    },
    [onSaveComplete, t, typeLabel]
  );

  // A write that fails keeps the intent, so a successful Retry still closes the dialog and toasts.
  const perform = useCallback(
    async (intent: Intent, write: () => SphereVisualWrite) => {
      intentRef.current = intent;
      const saved = await write();
      if (saved && intentRef.current === intent) {
        intentRef.current = null;
        succeeded(intent, saved);
      }
    },
    [succeeded]
  );

  const handleRetry = useCallback(async () => {
    const intent = intentRef.current;
    const saved = await visualRef.current.retry();
    if (saved && intent && intentRef.current === intent) {
      intentRef.current = null;
      succeeded(intent, saved);
    }
  }, [succeeded]);

  const handleGlyphChange = useCallback(
    (next: PickerValue) => void perform({}, () => visualRef.current.apply(next)),
    [perform]
  );

  const handleReset = useCallback(() => void perform({}, () => visualRef.current.reset()), [perform]);

  const handleUpload = useCallback(
    async (event: { files?: File[] }) => {
      const file = event.files?.[0];
      if (!file) return;
      await perform({ closeUpload: true }, () => visualRef.current.uploadPicture(file));
    },
    [perform]
  );

  const handleRemove = useCallback(
    () => void perform({ closeRemove: true }, () => visualRef.current.removePicture()),
    [perform]
  );

  const uploadRejectedText = (): string => {
    const { types, size } = describeLimits(pictureLimits);
    if (types && size) return t('sphere.visual.error.upload', { types, size });
    if (types) return t('sphere.visual.error.uploadTypes', { types });
    if (size) return t('sphere.visual.error.uploadSize', { size });
    return t('sphere.visual.error.uploadOther');
  };

  // What went wrong and what to do next, for the write that failed. A refusal or a rejected value
  // says why and offers no Retry, since the same write gets the same answer.
  const errorText = (failure: SphereVisualError): string => {
    switch (failure.kind) {
      case 'readOnly':
        return t('sphere.visual.error.forbidden');
      case 'unavailable':
        return t('sphere.visual.error.unavailable');
      case 'serviceUnavailable':
        return t('sphere.visual.error.serviceUnavailable');
      case 'slotUnavailable':
        return t('sphere.visual.error.slotUnavailable');
      case 'refused':
        return failure.operation === 'glyph' && failure.reason && REFUSAL_REASONS.has(failure.reason)
          ? t(`sphere.visual.error.refused.${failure.reason}`)
          : t('sphere.visual.error.refused.other');
      case 'invalid':
        if (failure.operation === 'upload') return uploadRejectedText();
        if (failure.operation === 'glyph') return t('sphere.visual.error.invalidGlyph');
        return t('sphere.visual.error.refused.other');
      case 'rateLimited':
        return failure.retryAt
          ? t('sphere.visual.error.rateLimitedUntil', { time: new Date(failure.retryAt) })
          : t('sphere.visual.error.rateLimited');
      case 'throttled':
        return t('sphere.visual.error.throttled');
      case 'notFound':
        return t('sphere.visual.error.notFound');
      default:
        if (failure.operation === 'upload') return t('sphere.visual.error.uploadFailed');
        if (failure.operation === 'remove') return t('sphere.visual.error.remove');
        return t('sphere.visual.error.save');
    }
  };

  // Where the glyph comes from. An own colour on the type's glyph says so; with no type glyph the
  // mark is the default glyph, whatever its colour.
  const provenance = (): string => {
    if (resolved.glyphSource === 'sphere') return t('sphere.visual.ownGlyph');
    if (resolved.glyphSource === 'default') return t('sphere.visual.usingDefaultGlyph');
    return resolved.glyphColorSource === 'sphere'
      ? t('sphere.visual.ownColourOnTypeGlyph', { type: typeLabel })
      : t('sphere.visual.usingTypeGlyph', { type: typeLabel });
  };

  // The picture is painted from the shared visual state, so a Replace or Remove shows at once. Until
  // the sphere cache answers, the mark is unknown: a skeleton holds its place rather than a glyph
  // that a picture would then replace.
  const mark = picture.resolving ? (
    <SkeletonAtom shape="circle" height="var(--spacing-xl)" />
  ) : (
    <SphereAvatar
      sphereId={sphereId}
      sphereName={sphereName}
      glyph={resolved.glyph}
      glyphColor={resolved.glyphColor}
      pictureUrl={picture.url ?? null}
      showInitials={false}
    />
  );

  const isRow = variant === 'row';
  const rootClass = `sphere-visual-control${isRow ? ' sphere-visual-control--row' : ''}`;

  if (readOnly || status === 'readOnly') {
    return (
      <div className={rootClass}>
        {mark}
        {status === 'readOnly' && <MessageAtom severity="error" text={t('sphere.visual.error.forbidden')} />}
      </div>
    );
  }

  const hasPicture = !!picture.url;
  const busy = status === 'saving';
  const savingReason = busy ? t('sphere.visual.saving') : undefined;
  const retryable = !!error && isRetryableSphereVisualError(error.kind);
  const panelError = error?.operation === 'glyph' ? error : null;
  const pictureError = error && error.operation !== 'glyph' ? error : null;

  const glyphSection = (
    <div className="sphere-visual-control__glyph">
      {hasPicture && (
        <span className="sphere-visual-control__section-label">{t('sphere.visual.shownWithoutPicture')}</span>
      )}
      <GlyphPickerMolecule
        glyph={resolved.glyph}
        glyphColor={resolved.glyphColor}
        onChange={handleGlyphChange}
        derived={!own}
        derivedFrom={typeLabel}
        provenanceLabel={provenance()}
        resetLabel={t('sphere.visual.reset')}
        onReset={handleReset}
        defaultGlyph={sphereType.glyph}
        defaultGlyphColor={sphereType.glyph_color}
        hueCommit="release"
        disabledReason={savingReason}
      />
    </div>
  );

  // While a picture is shown its section comes first, so its label says what the mark shows; the
  // glyph follows under "Shown when there's no picture".
  const pictureSection = canUploadPicture && (
    <div className="sphere-visual-control__picture-controls">
      <span className="sphere-visual-control__section-label">{t('sphere.visual.picture')}</span>
      {picture.checking && (
        <output className="sphere-visual-control__checking">
          {picture.checkStopped ? t('sphere.visual.checkStopped') : t('sphere.visual.checking')}
        </output>
      )}
      {pictureError && !uploadOpen && !removeOpen && (
        <div className="sphere-visual-control__error" role="alert">
          <MessageAtom severity="error" text={errorText(pictureError)} />
          {retryable && (
            <ButtonAtom variant="text" label={t('sphere.visual.retry')} onClick={() => void handleRetry()} />
          )}
        </div>
      )}
      <ButtonAtom
        label={hasPicture ? t('sphere.visual.replace') : t('sphere.visual.upload')}
        onClick={() => setUploadOpen(true)}
        disabled={busy}
        title={savingReason}
      />
      {hasPicture && (
        <ButtonAtom
          variant="text"
          label={t('sphere.visual.remove')}
          onClick={() => setRemoveOpen(true)}
          disabled={busy}
          title={savingReason}
        />
      )}
    </div>
  );

  const panel = (
    <div className="sphere-visual-control__panel" ref={panelRef}>
      <output className="sphere-visual-control__status" aria-live="polite">
        {busy ? t('sphere.visual.saving') : null}
      </output>

      {panelError && (
        <div className="sphere-visual-control__error" role="alert">
          <MessageAtom severity="error" text={errorText(panelError)} />
          {retryable && (
            <ButtonAtom variant="text" label={t('sphere.visual.retry')} onClick={() => void handleRetry()} />
          )}
        </div>
      )}

      {hasPicture && pictureSection}
      {glyphSection}
      {!hasPicture && pictureSection}
    </div>
  );

  return (
    <div className={rootClass}>
      <Toast ref={toastRef} position="bottom-right" />

      {isRow ? (
        <fieldset className="sphere-visual-control__fieldset" aria-labelledby={labelledBy ?? labelId}>
          {!labelledBy && (
            <span id={labelId} className="sphere-visual-control__section-label">
              {t('sphere.visual.label')}
            </span>
          )}
          {mark}
          {panel}
        </fieldset>
      ) : (
        <>
          <button
            type="button"
            ref={avatarButtonRef}
            className="sphere-visual-control__avatar"
            aria-label={t('sphere.visual.label')}
            aria-haspopup="dialog"
            aria-expanded={panelOpen}
            onClick={(e) => overlayRef.current?.toggle(e)}
          >
            {mark}
          </button>

          <OverlayPanelAtom
            ref={overlayRef}
            showCloseIcon
            onShow={() => setPanelOpen(true)}
            onHide={() => {
              setPanelOpen(false);
              avatarButtonRef.current?.focus();
            }}
          >
            {panel}
          </OverlayPanelAtom>
        </>
      )}

      <UploadAvatarDialogMolecule
        visible={uploadOpen}
        onHide={() => setUploadOpen(false)}
        onUpload={handleUpload}
        title={hasPicture ? t('sphere.visual.replaceTitle') : t('sphere.visual.uploadTitle')}
        // The slot's own limits, so the picker refuses what graph-service would.
        accept={pictureLimits?.mimeTypes?.length ? pictureLimits.mimeTypes.join(',') : 'image/*'}
        maxFileSize={pictureLimits?.maxSize ?? null}
        uploading={operation === 'upload'}
        uploadingLabel={t('sphere.visual.saving')}
        errorMessage={error?.operation === 'upload' ? errorText(error) : undefined}
      />

      <ConfirmDialogAtom
        visible={removeOpen}
        onHide={() => setRemoveOpen(false)}
        header={t('sphere.visual.confirmRemove.title')}
        message={
          <div className="sphere-visual-control__confirm">
            <span>{t('sphere.visual.confirmRemove.body', { name: sphereName ?? typeLabel })}</span>
            {error?.operation === 'remove' && <MessageAtom severity="error" text={errorText(error)} />}
          </div>
        }
        variant="danger"
        footer={
          <div className="sphere-visual-control__confirm-actions">
            <ButtonAtom
              variant="text"
              label={t('sphere.visual.confirmRemove.cancel')}
              onClick={() => setRemoveOpen(false)}
              disabled={busy}
              title={savingReason}
              autoFocus
            />
            <ButtonAtom
              variant="danger"
              label={t('sphere.visual.confirmRemove.confirm')}
              onClick={handleRemove}
              loading={operation === 'remove'}
              disabled={busy}
              title={operation === 'remove' ? undefined : savingReason}
            />
          </div>
        }
      />
    </div>
  );
};

export default SphereVisualControlMolecule;
